import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../helpers'
import { AddPropertyForm, AvailableEntrataProperty } from '../models/Forms'
import { FormUser } from '../models/Forms'

export interface OrganizationSummary {
    id: number
    name: string
    entrata_enabled: boolean
}

export interface SelfServeEntrataOnboardRequest {
    organization: number
    properties: {
        entrata_id: number
        name?: string
        address?: string
        city?: string
        state?: string
        zip_code?: string
    }[]
    regional_manager: FormUser
    property_manager?: FormUser
    other_users?: FormUser[]
    billing_email?: string | null
    start_date: string
    notes?: string | null
}

export const useOrganizationSummary = (organizationId: number | null) => {
    return useQuery({
        queryKey: ['organization-summary', organizationId],
        queryFn: async (): Promise<OrganizationSummary> => {
            const { data } = await axiosInstance.get(
                `organization/${organizationId}/`,
            )
            return data
        },
        enabled: Boolean(organizationId),
        staleTime: 5 * 60 * 1000,
    })
}

export const useAvailableEntrataProperties = (
    organizationId: number | null,
    enabled: boolean,
) => {
    return useQuery({
        queryKey: ['available-entrata-properties', organizationId],
        queryFn: async (): Promise<AvailableEntrataProperty[]> => {
            const { data } = await axiosInstance.get(
                `organization/${organizationId}/available-entrata-properties/`,
            )
            return data
        },
        enabled: Boolean(organizationId) && enabled,
        staleTime: 5 * 60 * 1000,
    })
}

export const useSelfServeEntrataOnboard = () => {
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: async (
            request: SelfServeEntrataOnboardRequest,
        ): Promise<AddPropertyForm[]> => {
            const { data } = await axiosInstance.post(
                'forms/self-serve-entrata-onboard/',
                request,
            )
            return data
        },
        onSuccess: (_data, variables) => {
            toast.success(
                'Onboarding started. You will receive a confirmation email when each property is ready.',
            )
            const submittedIds = new Set(
                variables.properties.map((p) => p.entrata_id),
            )
            queryClient.setQueryData<AvailableEntrataProperty[]>(
                ['available-entrata-properties', variables.organization],
                (old) =>
                    old
                        ? old.map((p) =>
                              submittedIds.has(p.entrata_id)
                                  ? { ...p, already_onboarded: true }
                                  : p,
                          )
                        : [],
            )
        },
        onError: (error: any) => {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                'Failed to start Entrata onboarding'
            toast.error(message)
        },
    })

    return {
        onboard: mutation.mutateAsync,
        isLoading: mutation.isLoading,
        isSuccess: mutation.isSuccess,
        reset: mutation.reset,
    }
}
